import { useMutation, useQuery } from "@tanstack/react-query";

import { useAppState } from "../../../../../context/StateContext";
import { Filters, Searches } from "../../../../../models/shipeezi.table";
import { Shipment } from "../../../../../models/shipments";
import ShipmentBookingService from "../../../../../services/shipment.booking.service";
import ShipmentService from "../../../../../services/shipment.service";
import { BookingKeys } from "../../../../../util/queries";

const useAction = () => {
  const { currentUser } = useAppState();
  const getAvailableBookings = (
    page?: number,
    pageSize?: number,
    searches?: Searches,
    filters?: Filters
  ) => {
    return useQuery({
      queryKey: BookingKeys.getAvaible([currentUser?.companyId, page, pageSize, searches, filters]),
      queryFn: async () => {
        const response = await ShipmentBookingService.getAvailable({
          page,
          pageSize,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          companyId: currentUser?.companyId,
          ...filters,
        });

        if (response && response.data) return response.data;
      },
      enabled: !!currentUser?.id,
    });
  };

  const useGetBooking = (callback?: (booking: Shipment) => void) => {
    return useMutation({
      mutationKey: BookingKeys.getById(),
      mutationFn: (id: number | string) =>
        ShipmentService.getById(Number(id), {
          attributes: [
            "id",
            "shipmentType",
            "containerId",
            "portOfLoadingId",
            "portOfDischargeId",
            "portOfReceiptId",
            "portOfDestinationId",
            "origin",
            "destination",
            "vesselName",
            "mbl",
            "hbl",
            "voyage",
            "shipTerms",
            "consigneeId",
            "incoterm",
            "billOfLanding",
            "airwayBill",
            "carrierId",
            "bound",
          ],
          include: JSON.stringify([
            {
              association: "portOfLoading",
              attributes: ["siteCode", "locationName"],
            },
            { association: "portOfDestination", attributes: ["siteCode", "locationName"] },
            {
              association: "portOfDischarge",
              attributes: ["siteCode", "locationName"],
            },
            { association: "portOfReceipt", attributes: ["siteCode", "locationName"] },
            { association: "consignee", attributes: ["code", "fullName", "name", "lastName"] },
            { association: "containers", attributes: ["container", "size", "seal", "grossWeight"] },
            {
              association: "company",
              attributes: ["id"],
              include: [{ association: "supplierAssociated", attributes: ["id", "name"] }],
            },
            {
              association: "purchaseOrders",
              include: [
                {
                  association: "consignee",
                },
                {
                  association: "supplier",
                },
                {
                  association: "purchaseOrderProducts",
                  include: [{ association: "product" }],
                },
              ],
            },
            {
              association: "cartons",
              include: [
                {
                  association: "cartonProducts",
                  include: [
                    { association: "purchaseOrder", required: true },
                    { association: "product", required: true },
                    { association: "purchaseOrderProduct", required: true, paranoid: false },
                  ],
                },
                {
                  association: "comments",
                  include: [
                    {
                      association: "user",
                      required: true,
                      attributes: ["fullName", "pictureId"],
                      include: [
                        { association: "picture", required: true, attributes: ["mediaLink"] },
                      ],
                    },
                  ],
                },
              ],
            },
          ]),
        }),
      onSuccess: (response) => {
        if (response && response?.data) {
          if (callback) callback(response?.data);
        }
      },
    });
  };

  return {
    getAvailableBookings,
    useGetBooking,
  };
};

export default useAction;
